import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRight, RefreshCw } from 'lucide-react'
import { Select } from '../../../admin/primitives.js'
import { TextInput } from '../../../admin/form.js'
import { fmtHeure, lireSupervision, type Lecture } from '../../api.js'
import { BandeauFige, BandeauLecture, EVENEMENT_SONDE_ECRITE, LIBELLE_ETAT, PastilleEtat, fmtAge, fmtNombre } from '../commun.js'
import { Vide, libelleSalle, optionsSalles } from './elements.js'
import { FicheAppareil } from './FicheAppareil.js'
import { useMenuSonde } from '../menu-sonde.js'
import {
  LIBELLE_CRITICITE, LIBELLE_CYCLE, LIBELLE_FAMILLE, estListeAppareils, libelleType,
  type AppareilBref, type ListeAppareils, type SalleRegistre,
} from './modele.js'

/**
 * ONGLET « APPAREILS » (21/10/2026) — les postes du registre de Sonde.
 *
 * Filtres côté Sonde (salle, famille, état, recherche, cycle de vie), 500 par
 * page, « Charger les suivants » au-delà : prêt pour des centaines de postes.
 * Chaque état porte son âge ; une donnée périmée est grise (jamais rouge).
 * Un clic ouvre la fiche d'édition (nom, salle, criticité, identifiants,
 * liens, exceptions, maintenances, Parc, retrait).
 */

const ETATS = ['ONLINE', 'DEGRADED', 'OFFLINE', 'OFF_EXPECTED', 'MAINTENANCE', 'UNKNOWN', 'STALE', 'UNAVAILABLE']
const CYCLES = ['active', 'spare', 'retired', 'ignored']

export function OngletAppareils(props: {
  peutGerer: boolean; salles: SalleRegistre[] | null; uidOuvert: string | null; onOuvrir: (uid: string | null) => void; onCompte: () => void
}) {
  const menu = useMenuSonde()
  const [salle, setSalle] = useState('')
  const [famille, setFamille] = useState('')
  const [etat, setEtat] = useState('')
  const [cycle, setCycle] = useState('active')
  const [saisie, setSaisie] = useState('')
  const [recherche, setRecherche] = useState('')
  const [lecture, setLecture] = useState<Lecture<ListeAppareils> | null>(null)
  const [items, setItems] = useState<AppareilBref[]>([])
  const [curseur, setCurseur] = useState<string | null>(null)
  const [luA, setLuA] = useState<string | null>(null)
  const [chargement, setChargement] = useState(false)

  // La recherche part 300 ms après la dernière frappe (Sonde filtre, pas le navigateur).
  useEffect(() => {
    const t = setTimeout(() => setRecherche(saisie.trim()), 300)
    return () => clearTimeout(t)
  }, [saisie])

  const filtres = useMemo(() => {
    const p = new URLSearchParams({ limit: '500', lifecycle: cycle })
    if (salle) p.set('zone', salle)
    if (famille) p.set('family', famille)
    if (etat) p.set('status', etat)
    if (recherche) p.set('q', recherche.slice(0, 100))
    return p.toString()
  }, [salle, famille, etat, cycle, recherche])

  const charger = useCallback(async (suite: string | null = null) => {
    setChargement(true)
    const l = await lireSupervision(`/v/devices?${filtres}${suite ? `&cursor=${encodeURIComponent(suite)}` : ''}`, estListeAppareils)
    setChargement(false)
    setLecture(l)
    if (l.etat !== 'ok') return
    setLuA(l.data.generated_at)
    setCurseur(l.data.next_cursor)
    setItems((avant) => (suite ? [...avant, ...l.data.items.filter((d) => !avant.some((a) => a.uid === d.uid))] : l.data.items))
  }, [filtres])

  useEffect(() => { void charger() }, [charger])
  // Renommé ou déplacé depuis le menu clic droit : la liste se relit.
  useEffect(() => {
    const relire = () => { void charger() }
    window.addEventListener(EVENEMENT_SONDE_ECRITE, relire)
    return () => window.removeEventListener(EVENEMENT_SONDE_ECRITE, relire)
  }, [charger])

  const donnees = lecture?.etat === 'ok' ? lecture.data : null
  const optsSalles = [{ value: '', label: 'Toutes les salles' }, ...optionsSalles(props.salles).slice(1)]
  const optsFamilles = [{ value: '', label: 'Toutes les familles' }, ...Object.entries(LIBELLE_FAMILLE).map(([value, label]) => ({ value, label }))]
  const optsEtats = [{ value: '', label: 'Tous les états' }, ...ETATS.map((e) => ({ value: e, label: LIBELLE_ETAT[e] ?? e }))]
  const optsCycles = CYCLES.map((c) => ({ value: c, label: c === 'active' ? 'Postes actifs' : LIBELLE_CYCLE[c] ?? c }))
  const filtre = !!(salle || famille || etat || recherche || cycle !== 'active')

  return (
    <section className="sv-rgs-onglet" aria-label="Appareils du registre">
      <div className="sv-rgs-outils">
        <Select compact ariaLabel="Salle" value={salle} options={optsSalles} onChange={setSalle} />
        <Select compact ariaLabel="Famille" value={famille} options={optsFamilles} onChange={setFamille} />
        <Select compact ariaLabel="État" value={etat} options={optsEtats} onChange={setEtat} />
        <Select compact ariaLabel="Cycle de vie" value={cycle} options={optsCycles} onChange={setCycle} />
        <div className="sv-rgs-outils__recherche">
          <TextInput value={saisie} onChange={setSaisie} placeholder="Nom, identifiant, IP…" ariaLabel="Rechercher un appareil" maxLength={100} />
        </div>
        <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void charger(); props.onCompte() }} disabled={chargement}>
          <RefreshCw size={14} aria-hidden="true" /> {chargement ? 'Lecture…' : 'Relire'}
        </button>
      </div>

      <p className="sv-rgs-source">
        Source : Sonde, registre{luA ? ` · état calculé à ${fmtHeure(luA)}` : ''}
        {donnees ? ` · ${fmtNombre(donnees.total)} poste${donnees.total > 1 ? 's' : ''}${filtre ? ' pour ces filtres' : ''}` : ''}
      </p>
      <BandeauFige collector={donnees?.collector} />
      <BandeauLecture lecture={lecture} depuis={luA} />

      {donnees && items.length === 0 ? (
        filtre ? <Vide titre="Aucun poste ne correspond à ces filtres." />
          : (
            <Vide titre="Le registre est vide.">
              Les appareils vus par Sonde attendent dans l’onglet « À valider » : validez-les pour qu’ils apparaissent ici et dans leur salle.
            </Vide>
          )
      ) : null}

      {items.length > 0 ? (
        <ul className="sv-rgs-liste sv-rgs-liste--appareils lc-panel">
          {items.map((a) => (
            <li key={a.uid}>
              <button type="button" className="sv-rgs-appareil" onClick={() => props.onOuvrir(a.uid)}
                aria-label={`Ouvrir la fiche de ${a.display_name}`}
                {...menu.appareil({ uid: a.uid, nom: a.display_name, zone_code: a.zone_code })}>
                <span className="sv-rgs-appareil__etat">
                  <PastilleEtat etat={a.state.display_state} libelle={a.state.display_label} />
                  <span className="sv-rgs-aide">{a.state.data_age_s === null ? '—' : fmtAge(a.state.data_age_s)}</span>
                </span>
                <span className="sv-rgs-appareil__corps">
                  <span className="sv-rgs-nom">{a.display_name}</span>
                  <span className="sv-rgs-meta">
                    <span className="sv-mono">{a.uid}</span>
                    <span>{libelleType(a.kind)} · {a.family_label}</span>
                    <span>{libelleSalle(props.salles, a.zone_code)}{a.zone_detail ? ` · ${a.zone_detail}` : ''}</span>
                    <span>{LIBELLE_CRITICITE[a.criticality] ?? a.criticality}{a.weighted ? ' · pèse dans l’état global' : ''}</span>
                    {a.lifecycle !== 'active' ? <span>{LIBELLE_CYCLE[a.lifecycle] ?? a.lifecycle}</span> : null}
                    {a.state.exception ? <span>Exception : {a.state.exception.reason}</span> : null}
                  </span>
                </span>
                <ChevronRight size={16} aria-hidden="true" className="sv-rgs-appareil__chevron" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {curseur ? (
        <p className="sv-rgs-suite">
          <button type="button" className="lc-btn" disabled={chargement} onClick={() => void charger(curseur)}>
            {chargement ? 'Lecture…' : `Charger les suivants (${fmtNombre(items.length)} sur ${fmtNombre(donnees?.total)})`}
          </button>
        </p>
      ) : null}

      {props.uidOuvert ? (
        <FicheAppareil uid={props.uidOuvert} peutGerer={props.peutGerer} salles={props.salles}
          onFermer={() => props.onOuvrir(null)}
          onChange={() => { void charger(); props.onCompte() }} />
      ) : null}
    </section>
  )
}
